'use client';

import { useCallback, useState, useRef, useMemo } from 'react';
import { encode, decodeGenerator } from 'gpt-tokenizer';
import SEO from '~/components/shared/SEO';
import FaqItem from "~/components/shared/FaqItem";

/*
const MODELS = {
  gpt4: `${process.env.NEXT_PUBLIC_DEFAULT_MODEL}`,
  gpt4Turbo: `${process.env.NEXT_PUBLIC_DEFAULT_MODEL}`,
  gpt4VisionPreview: `${process.env.NEXT_PUBLIC_DEFAULT_MODEL}`,
  gpt35Turbo: 'gpt-3.5-turbo',
};
*/

const pastelColors = [
  'rgba(107,64,216,.3)',
  'rgba(104,222,122,.4)',
  'rgba(244,172,54,.4)',
  'rgba(239,65,70,.4)',
  'rgba(39,181,234,.4)',
];

export default function Page() {
  const textRef = useRef<any>(null);
  const [counts, setCounts] = useState<any>({});
  const [encodedTokens, setEncodedTokens] = useState<number[]>([]);

  const decodedTokens = useMemo(() => {
    const tokens: (string | number)[] = [];
    for (const token of decodeGenerator(encodedTokens || [])) {
      tokens.push(token);
    }
    return tokens;
  }, [encodedTokens]);

  const onTextSubmit = useCallback(async () => {
    const text = textRef?.current?.value;
    const tokens = encode(text);
    setCounts({
      characters: text?.length,
      tokens: tokens?.length,
    });
    setEncodedTokens(tokens);
  }, []);

  return (
    <>
      <SEO
        title={`OpenAI GPT-4 Token Counter | Content Flywheel`}
        description={`Tired of calculating OpenAI tokens? Use our online tool to effortlessly convert text and receive an accurate token count for improved AI text processing.`}
        url={`https://www.contentflywheel.com/tools/openai-token-counter`}
      />
      <div className="flex justify-center overflow-x-hidden py-8">
        <div className="flex flex-col items-center w-full max-w-screen-3xl min-h-[500px] p-4 md:p-10">
          <h1 className="font-heading text-4xl font-bold text-center mb-6">
            OpenAI GPT-4 Token Counter
          </h1>
          <div>
            <p className="text-center mb-14 text-muted-foreground">
              Tired of calculating OpenAI tokens? Use our online tool to
              effortlessly convert text and receive an accurate token count
              for improved AI text processing.
            </p>
          </div>
          <div className="w-full lg:w-2/3 text-center">
            <div className="flex flex-col justify-center w-full">
              <textarea
                ref={textRef}
                className="textarea textarea-bordered dark:bg-[#08131c] mb-4 min-h-[500px] placeholder:italic text-base p-4"
                placeholder="Enter Content..."
              />
              <button
                className="btn btn-info mt-4 w-[200px] mx-auto"
                onClick={onTextSubmit}
              >
                Count Tokens
              </button>
            </div>
          </div>
          {!!counts?.tokens && (
            <div className="flex w-full justify-around lg:w-2/3 text-center mt-20 h-content">
              <div>
                <h3>Character Count: {counts?.characters}</h3>
              </div>
              <div>
                <h3>Token Count: {counts?.tokens}</h3>
              </div>
            </div>
          )}
          {decodedTokens && (
            <div className="w-full lg:w-2/3 text-center my-8 h-content">
              <TokenizedText tokens={decodedTokens} />
            </div>
          )}
          <div className="border-white/20 border-t w-full lg:w-2/3 py-20 max-w-4xl">
            <div id="frequently-asked-questions" className="flex flex-col">
              <h2 className="font-heading text-3xl font-bold text-center mb-6">
                Frequently Asked Questions (FAQs)
              </h2>
              {QUESTIONS.map((item, index) => {
                return <FaqItem key={index} item={item} />;
              })}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

const TokenizedText = ({ tokens }: { tokens: (string | number)[] }) => (
  <div
    className="h-content"
    style={{
      display: 'flex',
      flexWrap: 'wrap',
      fontFamily: `"Roboto Mono",sfmono-regular,consolas,liberation mono,menlo,courier,monospace`,
      width: '100%',
      overflowY: 'auto',
      padding: '8px',
      lineHeight: '1.5',
      alignContent: 'flex-start',
    }}
  >
    {tokens.map((token, index) => (
      <span
        key={index}
        style={{
          backgroundColor: pastelColors[index % pastelColors.length],
          padding: '0 0px',
          borderRadius: '3px',
          marginRight: '0px',
          marginBottom: '4px',
          display: 'inline-block',
          height: '1.5em',
        }}
      >
        {
          <pre>
            {String(token)
              .replaceAll(' ', '\u00A0')
              .replaceAll('\n', '<newline>')}
          </pre>
        }
      </span>
    ))}
  </div>
);

const QUESTIONS = [
  {
    question: 'What is the function of this AI tokenization tool?',
    answer:
      "This tool is designed to tokenize text using methodologies consistent with those of advanced language models like GPT-4, GPT-4-turbo, and GPT-3.5-turbo (ChatGPT). By pasting your text into the tool, it calculates the token count, helping you determine if your text exceeds the model's token limit, ensuring optimal text processing and analysis efficiency.",
  },
  {
    question: 'Can you explain what AI tokens are?',
    answer:
      "AI tokens are the fundamental units used by OpenAI's GPT models, such as ChatGPT, to understand and process text. Unlike simple word counts, tokens can represent words, parts of words, punctuation, or even emojis, making them a more nuanced measure of text length. This complexity allows GPT models to handle a wide range of languages and symbols, enhancing their understanding and generation of text.",
  },
  {
    question: 'How does one count tokens in a text?',
    answer:
      "Counting tokens involves analyzing your text with a tokenizer, a tool specifically designed to break down text into tokens. This process is user-friendly and requires just a simple action: copy and paste your text into the tokenizer. The tool then automatically provides the total token count, offering a clear view of your text's size in terms the AI model can understand.",
  },
  {
    question: 'What is the relationship between words and tokens in text?',
    answer:
      "The conversion from words to tokens isn't uniform across languages, leading to varying word-to-token ratios. For instance, in English, a single word typically translates to around 1.3 tokens. In contrast, languages like Spanish and French see a single word equating to approximately 2 tokens. This variance is due to linguistic structure differences, affecting how models tokenize text.",
  },
  {
    question: 'How are special characters and emojis represented as tokens?',
    answer:
      'In the realm of AI tokenization, punctuation marks such as commas, colons, and question marks are each counted as a single token. Special characters, including mathematical symbols and unique glyphs, may count as one to three tokens depending on their complexity. Emojis typically range from two to three tokens, reflecting their detailed information content. This nuanced approach allows for precise text analysis and processing by AI models.',
  },
  {
    question:
      'Why is tokenization important in AI and natural language processing?',
    answer:
      'Tokenization is a crucial step in AI and natural language processing (NLP) because it breaks down complex text into manageable units (tokens) for analysis. This process allows AI models, like those developed by OpenAI, to understand and generate human-like text. By converting text into tokens, models can efficiently process and interpret language nuances, idioms, and syntax, leading to more accurate and contextually relevant responses.',
  },
  {
    question: 'What impacts do different languages have on tokenization?',
    answer:
      'Different languages can significantly impact the tokenization process due to variations in syntax, grammar, and character set. For instance, languages like Chinese or Japanese, which do not use spaces to separate words, require more complex tokenization approaches to identify individual words and phrases. Understanding these differences is essential for developing and using AI models that can accurately process multilingual text.',
  },
  {
    question: 'How does tokenization affect the performance of ChatGPT?',
    answer:
      "The effectiveness of tokenization directly influences an AI model's performance. Proper tokenization ensures that the model comprehends the input text's structure and meaning, leading to more coherent and contextually appropriate outputs. Inadequate tokenization, however, can result in misunderstandings or irrelevant responses, as the model may not correctly interpret the text's nuances.",
  },
  {
    question:
      'Can tokenization help with understanding sentiment or emotion in text?',
    answer:
      "Yes, tokenization can play a role in sentiment analysis and emotion detection in text. By breaking down text into tokens, AI models can identify and weigh specific words or phrases that are indicative of sentiment or emotion. This detailed analysis allows for a more nuanced understanding of the text's tone, aiding in applications like customer feedback analysis, social media monitoring, and more.",
  },
  {
    question: 'Is there a limit to the number of tokens ChatGPT can process?',
    answer:
      "AI models like ChatGPT have a maximum token limit for each input and output session, which includes both the prompt and the generated response. This limit ensures efficient processing and resource allocation. Understanding this limit is crucial for users to optimize their queries and ensure their text is within the model's processing capabilities.",
  },
  {
    question: 'How can users optimize their text for tokenization?',
    answer:
      'Users can optimize their text for tokenization by being concise, using clear and straightforward language, and avoiding unnecessary jargon or complex syntax. This approach helps reduce the token count, making the text more accessible for AI processing. Additionally, understanding how different elements (like punctuation and emojis) contribute to the token count can guide users in crafting their input more effectively.',
  },
];
